import React, { useState } from 'react';
import { Button, Card, Typography, message } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

const SimpleAuthTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();

  const handleManualTokenTest = async () => {
    setLoading(true);
    try {
      // Get the auth URL from backend
      const response = await fetch('http://localhost:3000/api/v1/auth/google');
      const { authUrl } = await response.json();
      
      message.info('Opening authentication in new window...');
      
      // Open in new window
      const authWindow = window.open(authUrl, 'auth', 'width=500,height=600');
      
      if (!authWindow) {
        throw new Error('Failed to open authentication window. Please allow popups.');
      }

      // Poll for when the window closes or gets redirected
      const checkWindow = setInterval(() => {
        try {
          if (authWindow.closed) {
            clearInterval(checkWindow);
            message.info('Authentication window closed. Please check if login was successful.');
            setLoading(false);
            return;
          }

          // Try to get the URL of the auth window
          const authUrl = authWindow.location.href;
          if (authUrl && authUrl.includes('/auth/callback')) {
            const urlParams = new URLSearchParams(authWindow.location.search);
            const token = urlParams.get('token');
            
            if (token) {
              // Close the auth window
              authWindow.close();
              clearInterval(checkWindow);
              
              // Set the token in localStorage and refresh the page
              localStorage.setItem('auth_token', token);
              message.success('Authentication successful! Refreshing page...');
              
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            }
          }
        } catch (e) {
          // This is expected due to CORS when checking the URL
          // Just continue polling until window closes
        }
      }, 1000);

      // Timeout after 5 minutes
      setTimeout(() => {
        if (!authWindow.closed) {
          authWindow.close();
          clearInterval(checkWindow);
          setLoading(false);
          message.error('Authentication timed out');
        }
      }, 300000);

    } catch (error: any) {
      console.error('Authentication failed:', error);
      message.error(error.message || 'Authentication failed');
      setLoading(false);
    }
  };

  if (isAuthenticated && user) {
    return (
      <div style={{ maxWidth: 400, margin: '50px auto', padding: '20px' }}>
        <Card>
          <Title level={3}>✅ Authentication Success!</Title>
          <div style={{ marginBottom: 16 }}>
            <Text strong>User ID:</Text> {user.id}
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Email:</Text> {user.email}
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Name:</Text> {user.firstName} {user.lastName}
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Role:</Text> {user.role}
          </div>
          <Button type="primary" danger onClick={logout} block>
            Logout
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: '50px auto', padding: '20px' }}>
      <Card>
        <Title level={3}>Simple Authentication Test</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          This test uses a popup window to avoid redirect issues.
        </Text>
        
        <Button
          type="primary"
          icon={<GoogleOutlined />}
          loading={loading}
          onClick={handleManualTokenTest}
          block
          size="large"
        >
          Login with Google (Popup)
        </Button>

        <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <Text style={{ fontSize: '12px' }}>
            <strong>Instructions:</strong><br />
            1. Click the button above<br />
            2. Allow popups if prompted<br />
            3. Complete authentication in the popup<br />
            4. The popup will close and you'll be logged in
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default SimpleAuthTest;